import React, { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";

const DROPDOWN_WIDTH = 280;
const DROPDOWN_HEIGHT = 196;

const SelectChevron = () => (
  <svg
    className="pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M19 9l-7 7-7-7"
    />
  </svg>
);

const stripPeriodSuffix = (input) =>
  String(input || "")
    .trim()
    .replace(/\s?(AM|PM)$/i, "")
    .replace(/(am|pm)$/i, "")
    .trim();

const parseTimeInput = (input) => {
  if (!input) return null;

  const cleaned = String(input).replace(/\s+/g, "").toLowerCase();

  const patterns = [
    /^(\d{1,2}):(\d{2})(am|pm)$/,
    /^(\d{1,2})(\d{2})(am|pm)$/,
    /^(\d{1,2})(am|pm)$/,
    /^(\d{1,2}):(\d{2})$/,
  ];

  for (const pattern of patterns) {
    const match = cleaned.match(pattern);
    if (match) {
      let hour = parseInt(match[1], 10);
      let minute = match[2] ? parseInt(match[2], 10) : 0;
      let period = match[3] || null;

      if (period) {
        if (hour < 1 || hour > 12) return null;
        if (minute > 59) return null;
      } else if (hour > 12) {
        if (hour > 23 || minute > 59) return null;
        return `${hour.toString().padStart(2, "0")}:${minute
          .toString()
          .padStart(2, "0")}`;
      } else {
        period = "am";
      }

      let hour24 = hour;
      if (period === "am" && hour === 12) hour24 = 0;
      else if (period === "pm" && hour !== 12) hour24 += 12;

      return `${hour24.toString().padStart(2, "0")}:${minute
        .toString()
        .padStart(2, "0")}`;
    }
  }

  return null;
};

const convertTo12HourDisplay = (time24) => {
  if (!time24) return "";

  const parsed = parseTimeInput(time24) || time24;
  const [hours, minutes] = String(parsed).split(":");
  if (!hours || minutes === undefined) return String(time24);

  const hour24 = parseInt(hours, 10);
  if (Number.isNaN(hour24)) return String(time24);

  let hour12 = hour24;
  let period = "AM";

  if (hour24 === 0) {
    hour12 = 12;
  } else if (hour24 === 12) {
    hour12 = 12;
    period = "PM";
  } else if (hour24 > 12) {
    hour12 = hour24 - 12;
    period = "PM";
  }

  return `${hour12}:${minutes.padStart(2, "0")} ${period}`;
};

const convertTo24Hour = (hour, minute, period) => {
  let hour24 = parseInt(hour, 10);

  if (period === "AM" && hour24 === 12) {
    hour24 = 0;
  } else if (period === "PM" && hour24 !== 12) {
    hour24 += 12;
  }

  return `${hour24.toString().padStart(2, "0")}:${minute}`;
};

const TimePicker = ({
  value,
  onChange,
  error,
  placeholder,
  className = "",
  size = "md",
  dropdownOnly = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [displayValue, setDisplayValue] = useState("");
  const [selectedHour, setSelectedHour] = useState("12");
  const [selectedMinute, setSelectedMinute] = useState("00");
  const [selectedPeriod, setSelectedPeriod] = useState("AM");
  const [dropdownPosition, setDropdownPosition] = useState(null);
  const containerRef = useRef(null);
  const portalRef = useRef(null);

  const syncFromValue = (nextValue) => {
    if (!nextValue) {
      setDisplayValue("");
      setSelectedHour("12");
      setSelectedMinute("00");
      setSelectedPeriod("AM");
      return;
    }

    const time24 = parseTimeInput(nextValue) || nextValue;
    setDisplayValue(convertTo12HourDisplay(time24));

    const [hours, minutes] = String(time24).split(":");
    const hour24 = parseInt(hours, 10);
    if (Number.isNaN(hour24)) return;

    if (hour24 === 0) {
      setSelectedHour("12");
      setSelectedPeriod("AM");
    } else if (hour24 < 12) {
      setSelectedHour(hour24.toString());
      setSelectedPeriod("AM");
    } else if (hour24 === 12) {
      setSelectedHour("12");
      setSelectedPeriod("PM");
    } else {
      setSelectedHour((hour24 - 12).toString());
      setSelectedPeriod("PM");
    }

    setSelectedMinute(String(minutes || "00").padStart(2, "0"));
  };

  useEffect(() => {
    syncFromValue(value);
  }, [value]);

  const updateDropdownPosition = useCallback(() => {
    const trigger = containerRef.current;
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const width = Math.max(rect.width, DROPDOWN_WIDTH);
    let left = rect.left;
    let top = rect.bottom + 6;

    if (left + width > window.innerWidth - 12) {
      left = window.innerWidth - width - 12;
    }
    if (left < 12) left = 12;

    if (top + DROPDOWN_HEIGHT > window.innerHeight - 12) {
      top = rect.top - DROPDOWN_HEIGHT - 6;
    }
    if (top < 12) top = 12;

    setDropdownPosition({ top, left, width });
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;

    updateDropdownPosition();

    const handleReposition = () => updateDropdownPosition();
    window.addEventListener("resize", handleReposition);
    window.addEventListener("scroll", handleReposition, true);

    return () => {
      window.removeEventListener("resize", handleReposition);
      window.removeEventListener("scroll", handleReposition, true);
    };
  }, [isOpen, updateDropdownPosition]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const inTrigger = containerRef.current?.contains(event.target);
      const inPortal = portalRef.current?.contains(event.target);
      if (!inTrigger && !inPortal) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleTimeSelect = () => {
    const time24 = convertTo24Hour(
      selectedHour,
      selectedMinute,
      selectedPeriod
    );
    setDisplayValue(convertTo12HourDisplay(time24));
    onChange(time24);
    setIsOpen(false);
  };

  const openDropdown = () => {
    setIsOpen(true);
    window.requestAnimationFrame(updateDropdownPosition);
  };

  const formatTimeInput = (input) => {
    if (input.includes(":")) {
      const parts = input.split(":");
      if (parts.length === 2) {
        let hour = parseInt(parts[0], 10);
        let minuteStr = parts[1].replace(/[^\d]/g, "");

        if (hour > 12) {
          hour = 12;
        }
        if (hour < 1) {
          hour = 1;
        }

        if (minuteStr.length > 0) {
          let minute = parseInt(minuteStr, 10);
          if (minute > 59) {
            minute = 59;
          }
          return {
            formatted:
              hour.toString() + ":" + minute.toString().padStart(2, "0"),
          };
        } else {
          return { formatted: hour.toString() + ":" };
        }
      }
    }

    const digitsOnly = input.replace(/[^\d]/g, "");

    if (digitsOnly.length === 0) {
      return { formatted: "" };
    }

    let formatted = "";

    if (digitsOnly.length === 1) {
      formatted = digitsOnly;
    } else if (digitsOnly.length === 2) {
      let hour = parseInt(digitsOnly, 10);
      if (hour > 12) {
        let firstDigit = parseInt(digitsOnly.charAt(0), 10);
        let secondDigit = parseInt(digitsOnly.charAt(1), 10);
        if (secondDigit > 5) {
          secondDigit = 5;
        }
        formatted = firstDigit.toString() + ":" + secondDigit.toString();
      } else {
        formatted = digitsOnly + ":";
      }
    } else if (digitsOnly.length === 3) {
      let hour = parseInt(digitsOnly.substring(0, 2), 10);
      let minute = digitsOnly.substring(2);

      if (hour > 12) {
        hour = parseInt(digitsOnly.substring(0, 1), 10);
        minute = digitsOnly.substring(1);
        let minuteNum = parseInt(minute, 10);
        if (minuteNum > 59) {
          minute = "59";
        }
        formatted = hour.toString() + ":" + minute;
      } else {
        let minuteNum = parseInt(minute, 10);
        if (minuteNum > 5) {
          minute = "5";
        }
        formatted = hour.toString() + ":" + minute;
      }
    } else if (digitsOnly.length === 4) {
      let hour = parseInt(digitsOnly.substring(0, 2), 10);
      let minute = digitsOnly.substring(2);

      if (hour > 12) {
        hour = parseInt(digitsOnly.substring(0, 1), 10);
        minute = digitsOnly.substring(1, 3);
        let minuteNum = parseInt(minute, 10);
        if (minuteNum > 59) {
          minute = "59";
        }
        formatted = hour.toString() + ":" + minute;
      } else {
        let minuteNum = parseInt(minute, 10);
        if (minuteNum > 59) {
          minute = "59";
        }
        formatted = hour.toString() + ":" + minute;
      }
    }

    return { formatted };
  };

  const handleInputChange = (e) => {
    const inputValue = e.target.value;

    if (inputValue.length < displayValue.length) {
      const wouldCreateIncomplete =
        inputValue.endsWith(" A") ||
        inputValue.endsWith(" P") ||
        (inputValue.endsWith("A") && inputValue.includes(":")) ||
        (inputValue.endsWith("P") && inputValue.includes(":"));

      if (wouldCreateIncomplete) {
        const cleanedValue = inputValue.replace(/\s?[AP]$/, "");
        setDisplayValue(cleanedValue);
        return;
      }

      setDisplayValue(inputValue);
      return;
    }

    const hasIncompletePattern =
      inputValue.endsWith(" A") ||
      inputValue.endsWith(" P") ||
      (inputValue.endsWith("A") && inputValue.includes(":")) ||
      (inputValue.endsWith("P") && inputValue.includes(":"));

    if (hasIncompletePattern) {
      const lastChar = inputValue.charAt(inputValue.length - 1).toLowerCase();
      if (lastChar === "a" || lastChar === "p") {
        const timeOnly = stripPeriodSuffix(inputValue).replace(/[^\d:]/g, "");
        const newPeriod = lastChar === "a" ? " AM" : " PM";
        const newValue = `${timeOnly}${newPeriod}`;
        setDisplayValue(newValue);

        const parsed = parseTimeInput(newValue);
        if (parsed) {
          onChange(parsed);
        }
        return;
      } else {
        return;
      }
    }

    const hasCompleteAMPM =
      displayValue.includes(" AM") || displayValue.includes(" PM");
    const lastChar = inputValue.charAt(inputValue.length - 1);

    if (hasCompleteAMPM && /\d/.test(lastChar)) {
      return;
    }

    const isAMPMChar =
      lastChar.toLowerCase() === "a" || lastChar.toLowerCase() === "p";

    if (isAMPMChar) {
      const timePartOnly = stripPeriodSuffix(displayValue).replace(/[^\d:]/g, "");
      const newPeriod = lastChar.toLowerCase() === "a" ? " AM" : " PM";
      const newValue = `${timePartOnly}${newPeriod}`;
      setDisplayValue(newValue);

      const parsed = parseTimeInput(newValue);
      if (parsed) {
        onChange(parsed);
      }
      return;
    }

    if (/[a-zA-Z]/.test(lastChar) && /\d:\d/.test(displayValue)) {
      return;
    }

    const digitsOnly = inputValue.replace(/[^\d]/g, "");

    if (digitsOnly.length <= 4) {
      const { formatted } = formatTimeInput(inputValue);
      setDisplayValue(formatted);

      const parsed = parseTimeInput(formatted);
      if (parsed) {
        onChange(parsed);
      }
    } else {
      const { formatted } = formatTimeInput(digitsOnly.slice(0, 4));
      setDisplayValue(formatted);
      const parsed = parseTimeInput(formatted);
      if (parsed) {
        onChange(parsed);
      }
    }
  };

  const handleInputBlur = (e) => {
    const inPortal = portalRef.current?.contains(e.relatedTarget);
    if (inPortal) return;

    const parsed = parseTimeInput(displayValue);
    if (parsed) {
      onChange(parsed);
      setDisplayValue(convertTo12HourDisplay(parsed));
      return;
    }

    if (value) {
      syncFromValue(value);
    } else {
      setDisplayValue("");
      onChange("");
    }
  };

  const handleInputFocus = () => {
    if (dropdownOnly) {
      openDropdown();
      return;
    }

    setTimeout(() => {
      if (document.activeElement) {
        document.activeElement.select();
      }
    }, 0);
  };

  const handleTriggerClick = () => {
    setIsOpen((open) => {
      const next = !open;
      if (next) {
        window.requestAnimationFrame(updateDropdownPosition);
      }
      return next;
    });
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      setIsOpen(false);
      e.target.blur();
    } else if (e.key === "Escape") {
      setIsOpen(false);
      e.target.blur();
    }
  };

  const hours = Array.from({ length: 12 }, (_, i) => (i + 1).toString());
  const minutes = Array.from({ length: 60 }, (_, i) =>
    i.toString().padStart(2, "0")
  );

  const inputSizeClass =
    size === "sm" ? "h-9 px-3 text-sm" : "h-10 px-3.5 text-sm";

  const selectClassName =
    "h-9 w-full min-w-0 appearance-none rounded-lg border border-gray-200 bg-white px-2 pr-7 text-center text-sm font-medium text-gray-800 transition focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/10";

  const fieldLabelClassName =
    "mb-1.5 block text-center text-xs font-medium text-gray-500";

  const dropdownPanel = isOpen && dropdownPosition ? (
    <div
      ref={portalRef}
      style={{
        position: "fixed",
        top: dropdownPosition.top,
        left: dropdownPosition.left,
        width: dropdownPosition.width,
        zIndex: 9999,
      }}
      className="rounded-xl border border-gray-200 bg-white p-4 shadow-xl"
    >
      <div className="grid grid-cols-3 gap-2.5">
        <div className="min-w-0">
          <label className={fieldLabelClassName}>Hour</label>
          <div className="relative">
            <select
              value={selectedHour}
              onChange={(e) => setSelectedHour(e.target.value)}
              className={selectClassName}
            >
              {hours.map((hour) => (
                <option key={hour} value={hour}>
                  {hour}
                </option>
              ))}
            </select>
            <SelectChevron />
          </div>
        </div>

        <div className="min-w-0">
          <label className={fieldLabelClassName}>Minute</label>
          <div className="relative">
            <select
              value={selectedMinute}
              onChange={(e) => setSelectedMinute(e.target.value)}
              className={selectClassName}
            >
              {minutes.map((minute) => (
                <option key={minute} value={minute}>
                  {minute}
                </option>
              ))}
            </select>
            <SelectChevron />
          </div>
        </div>

        <div className="min-w-0">
          <label className={fieldLabelClassName}>Period</label>
          <div className="relative">
            <select
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              className={selectClassName}
            >
              <option value="AM">AM</option>
              <option value="PM">PM</option>
            </select>
            <SelectChevron />
          </div>
        </div>
      </div>

      <div className="mt-4 flex items-center justify-end gap-2 border-t border-gray-100 pt-3">
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => setIsOpen(false)}
          className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-600 transition hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={handleTimeSelect}
          className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-primary/90"
        >
          Select
        </button>
      </div>
    </div>
  ) : null;

  return (
    <div className={`relative ${className}`} ref={containerRef}>
      <div className="relative">
        <input
          type="text"
          value={displayValue}
          readOnly={dropdownOnly}
          onChange={dropdownOnly ? undefined : handleInputChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          onKeyDown={handleKeyDown}
          onClick={dropdownOnly ? handleTriggerClick : undefined}
          className={`w-full rounded-lg border bg-white pr-9 font-medium text-gray-800 placeholder:font-normal placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/10 focus:border-primary ${inputSizeClass} ${
            dropdownOnly ? "cursor-pointer" : ""
          } ${error ? "border-red-500" : "border-gray-200"}`}
          placeholder={placeholder || "Select time"}
        />
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={handleTriggerClick}
          aria-label="Open time picker"
          aria-expanded={isOpen}
          className="absolute right-2.5 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-md text-gray-400 transition hover:bg-gray-50 hover:text-gray-600"
        >
          <svg
            className="h-4 w-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>
      </div>

      {typeof document !== "undefined" &&
        createPortal(dropdownPanel, document.body)}

      {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
    </div>
  );
};

export default TimePicker;
